import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Drawer } from "../Drawer";
import { Icon } from "../ui/Icon";
import { trpc } from "../../lib/trpc";

type Severity = "low" | "medium" | "high" | "critical";
const SEV_LABELS: Record<Severity, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const SEV_OPTS = Object.entries(SEV_LABELS) as [Severity, string][];

interface Props {
  open: boolean;
  centerId: string;
  centerName: string;
  onClose: () => void;
}

/** Bir üretim merkezinin arıza tipi kataloğunu yönetir (ekle/düzenle/pasifleştir/sil). */
export function FaultCatalogDrawer({ open, centerId, centerName, onClose }: Props) {
  const qc = useQueryClient();
  const catQ = trpc.faultCatalog.list.useQuery({ centerId }, { enabled: open && !!centerId, staleTime: 10_000 });
  const createMut = trpc.faultCatalog.create.useMutation();
  const updateMut = trpc.faultCatalog.update.useMutation();
  const toggleMut = trpc.faultCatalog.toggleActive.useMutation({ onSuccess: () => qc.invalidateQueries() });
  const deleteMut = trpc.faultCatalog.delete.useMutation();

  const [editId, setEditId] = useState<string | null>(null);
  const [label, setLabel] = useState("");
  const [code, setCode] = useState("");
  const [description, setDescription] = useState("");
  const [severity, setSeverity] = useState<Severity | "">("");
  const [err, setErr] = useState<string | null>(null);

  const items = (catQ.data ?? []) as Array<{
    id: string; code: string | null; label: string; description: string | null; defaultSeverity: Severity | null; isActive: boolean;
  }>;
  const busy = createMut.isPending || updateMut.isPending;

  function resetForm() { setEditId(null); setLabel(""); setCode(""); setDescription(""); setSeverity(""); setErr(null); }
  useEffect(() => { if (open) resetForm(); /* eslint-disable-next-line */ }, [open, centerId]);

  function loadForEdit(x: (typeof items)[number]) {
    setEditId(x.id); setLabel(x.label); setCode(x.code ?? ""); setDescription(x.description ?? ""); setSeverity(x.defaultSeverity ?? ""); setErr(null);
  }

  async function save() {
    setErr(null);
    if (!label.trim()) { setErr("Arıza tipi adı zorunlu."); return; }
    const payload = { label: label.trim(), code: code.trim() || null, description: description.trim() || null, defaultSeverity: severity || null };
    try {
      if (editId) await updateMut.mutateAsync({ id: editId, ...payload });
      else await createMut.mutateAsync({ centerId, ...payload });
      await qc.invalidateQueries();
      resetForm();
    } catch (e) { setErr((e as Error).message); }
  }

  function remove(id: string) {
    deleteMut.mutate({ id }, { onError: (e) => setErr(e.message), onSuccess: () => { deleteMut.reset(); qc.invalidateQueries(); if (editId === id) resetForm(); } });
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Arıza Kataloğu"
      subtitle={centerName ? `Merkez: ${centerName}` : undefined}
      footer={<button type="button" className="btn-md ghost" onClick={onClose}>Kapat</button>}
    >
      {err ? <div className="form-banner-err" style={{ marginBottom: 12 }}>{err}</div> : null}

      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--neutral-800)", marginBottom: 8 }}>Tanımlı Arıza Tipleri ({items.length})</div>
      {catQ.isLoading ? (
        <div style={{ padding: 12, color: "var(--neutral-500)", fontSize: 13 }}>Yükleniyor…</div>
      ) : items.length === 0 ? (
        <div style={{ padding: 12, color: "var(--neutral-500)", fontSize: 13 }}>Henüz arıza tipi yok. Aşağıdan ekleyin (ör. Motor arızası, Bant kopması, Elektrik kesintisi).</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {items.map((x) => (
            <div key={x.id} className="d-card" style={{ padding: "8px 12px", display: "flex", alignItems: "center", gap: 10, opacity: x.isActive ? 1 : 0.55 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  {x.code ? <span style={{ fontFamily: "var(--font-mono)", color: "var(--neutral-500)", fontWeight: 500 }}>{x.code} · </span> : null}
                  {x.label}
                  {x.defaultSeverity ? <span style={{ color: "var(--neutral-500)", fontWeight: 400 }}> · {SEV_LABELS[x.defaultSeverity]}</span> : null}
                </div>
                {x.description ? <div style={{ fontSize: 11, color: "var(--neutral-500)" }}>{x.description}{!x.isActive ? " · pasif" : ""}</div> : (!x.isActive ? <div style={{ fontSize: 11, color: "var(--neutral-500)" }}>pasif</div> : null)}
              </div>
              <button type="button" className="iact" aria-label="Düzenle" title="Düzenle" onClick={() => loadForEdit(x)}><Icon name="edit" size={14} /></button>
              <button type="button" className={x.isActive ? "iact danger" : "iact success"} aria-label={x.isActive ? "Pasifleştir" : "Aktifleştir"} title={x.isActive ? "Pasifleştir" : "Aktifleştir"} onClick={() => toggleMut.mutate({ id: x.id, isActive: !x.isActive })} disabled={toggleMut.isPending}><Icon name={x.isActive ? "archive" : "check"} size={14} /></button>
              <button type="button" className="iact danger" aria-label="Sil" title="Sil" onClick={() => remove(x.id)} disabled={deleteMut.isPending}><Icon name="trash" size={14} /></button>
            </div>
          ))}
        </div>
      )}

      <div style={{ borderTop: "1px solid var(--neutral-200)", paddingTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 10 }}>{editId ? "Arıza Tipini Düzenle" : "Yeni Arıza Tipi"}</div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="fc-label">Arıza Tipi <span className="req">*</span></label>
            <input id="fc-label" className="form-input" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="örn. Motor arızası" maxLength={160} />
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="fc-code">Kod</label>
            <input id="fc-code" className="form-input" value={code} onChange={(e) => setCode(e.target.value)} placeholder="opsiyonel" maxLength={40} />
          </div>
        </div>
        <div className="form-row">
          <div className="form-field">
            <label className="lbl" htmlFor="fc-sev">Varsayılan Önem</label>
            <select id="fc-sev" className="form-select" value={severity} onChange={(e) => setSeverity(e.target.value as Severity | "")}>
              <option value="">—</option>
              {SEV_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-field">
            <label className="lbl" htmlFor="fc-desc">Açıklama</label>
            <input id="fc-desc" className="form-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="opsiyonel" maxLength={500} />
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
          <button type="button" className="btn-md primary" onClick={save} disabled={busy}>
            <Icon name={editId ? "check" : "plus"} size={14} />{busy ? "Kaydediliyor…" : editId ? "Güncelle" : "Ekle"}
          </button>
          {editId ? <button type="button" className="btn-md ghost" onClick={resetForm} disabled={busy}>Vazgeç</button> : null}
        </div>
      </div>
    </Drawer>
  );
}
